'use client'

/**
 * The story clock in the browser (plans/00 B3; plans/03 A3; plans/07 4.2).
 *
 * `useStory()` polls `GET /api/dr/story` every 3 seconds, every 2 seconds
 * while the autopilot runs (one shared loop for the whole window), and
 * refetches at once on a `dr.story.step` frame or a persona change. It is the
 * only source of "now" in the browser: pages never compute the clock
 * themselves. `onStoryMoved` tells shell data the step changed, however the
 * window learnt it (a frame or a poll).
 */

import { useEffect } from 'react'
import { createPolledResource } from '@/lib/polled'
import { getPersona, usePersona } from '@/lib/persona'
import { onStoryStep } from '@/hooks/useAutoStream'
import { BEFORE_EARTHQUAKE_LABEL, fmtElapsed } from '@/lib/elapsed'
import type { StoryState } from '@/lib/dr-types'

export const STORY_POLL_MS = 3000
/** Poll pace while the autopilot runs, so its moves show within a couple of seconds. */
export const STORY_AUTOPILOT_POLL_MS = 2000

/** The first step (before the earthquake) and the last one (the situation report). */
export const STORY_START_STEP = 's0'
export const STORY_END_STEP = 'sr'

/** Steps where the autopilot has nothing counting: before the earthquake and the report at the end. */
const AUTOPILOT_IDLE_STEPS = new Set<string>([STORY_START_STEP, STORY_END_STEP])

/** True while the autopilot is on and the story is between the first alert and the report. */
export function autopilotRunning(story: Pick<StoryState, 'step_id' | 'autopilot'> | null): boolean {
  const autopilot = story?.autopilot
  return !!autopilot && autopilot.enabled && !AUTOPILOT_IDLE_STEPS.has(story.step_id)
}

const storyResource = createPolledResource<StoryState>('/api/dr/story', STORY_POLL_MS)

/** Refetch the story now (after the Presenter moves a step in this window). */
export function refetchStory(): Promise<void> {
  return storyResource.refetch()
}

let lastPersona: string | null = null
let lastMoveKey: string | null = null
const moveListeners = new Set<() => void>()

/** Called when the story step or sequence changes in this window (not on the first answer). */
export function onStoryMoved(listener: () => void): () => void {
  moveListeners.add(listener)
  return () => {
    moveListeners.delete(listener)
  }
}
let frameSubscribers = 0
let unsubscribeFrames: (() => void) | null = null

export interface UseStoryResult {
  story: StoryState | null
  loading: boolean
  /** HTTP status of the last failure (501 until the story endpoint is built). */
  errorStatus: number | null
  /** True when the story clock is before the earthquake (or not loaded). */
  beforeQuake: boolean
  /** "+16 h 20 min", or "Before the earthquake". Null while loading. */
  elapsedLabel: string | null
  refetch: () => Promise<void>
}

export function useStory(): UseStoryResult {
  const snapshot = storyResource.useResource()
  const { persona } = usePersona()

  // Refetch when this window's persona changes (support sees more fields).
  useEffect(() => {
    const current = getPersona()
    if (lastPersona !== null && lastPersona !== current) void storyResource.refetch()
    lastPersona = current
  }, [persona])

  // One frame listener for the whole window, however many components read the story.
  useEffect(() => {
    frameSubscribers += 1
    if (frameSubscribers === 1) unsubscribeFrames = onStoryStep(() => storyResource.refetchSoon(150))
    return () => {
      frameSubscribers -= 1
      if (frameSubscribers === 0 && unsubscribeFrames) {
        unsubscribeFrames()
        unsubscribeFrames = null
      }
    }
  }, [])

  const story = snapshot.data
  const running = autopilotRunning(story)
  const moveKey = story ? `${story.seq}|${story.step_id}` : null

  // A faster poll while the autopilot runs; one loop, so every reader sets the same pace.
  useEffect(() => {
    storyResource.setIntervalMs(running ? STORY_AUTOPILOT_POLL_MS : STORY_POLL_MS)
  }, [running])

  // Tell shell data the story moved, once per move for the whole window.
  useEffect(() => {
    if (moveKey === null || moveKey === lastMoveKey) return
    const first = lastMoveKey === null
    lastMoveKey = moveKey
    if (!first) moveListeners.forEach((listener) => listener())
  }, [moveKey])

  const beforeQuake = !story || story.clock_min < 0
  const elapsedLabel = story
    ? story.clock_min < 0
      ? BEFORE_EARTHQUAKE_LABEL
      : story.at_label || fmtElapsed(story.clock_min)
    : null

  return {
    story,
    loading: snapshot.loading,
    errorStatus: snapshot.errorStatus,
    beforeQuake,
    elapsedLabel,
    refetch: storyResource.refetch,
  }
}
